import React, {useState} from "react";
import {Link} from "react-router-dom";
import {useAtomValue} from "jotai";
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import {Box, Button, Collapse, IconButton, Stack, Typography} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import {Order} from "../../types/domain/order";
import {OrderStatus} from "../../types/domain/orderStatus";
import {useOrderManagement} from "../../hooks/ordersHooks";
import {swallowException} from "../../utils/appUtils";
import {FieldsTable} from "../fields/FieldsTable";
import {OrderCreateDialog} from "../dialogs/OrderCreateDialog";
import {isUserHaveAnyOfGlobalRoles} from "../../utils/accessControlUtils";
import {UserGlobalRole} from "../../types/domain/user";
import {authenticatedUserLoadable} from "../../store/authenticatedUserStore";
import {getOrderPeriod} from "../../utils/orderUtils";
import {Field} from "../../types/domain/field";
import {OrderStatusCircle} from "./OrderStatusCircle";
import {EditIcon} from "../icons/EditIcon";
import {useMeasureSystemUtils} from "../../hooks/measureSystemUtilsHooks";
import {useUserPreferences} from "../../hooks/userPreferencesHooks";
import {useResourceValueOrDefault} from "../../hooks/resourceHooks";
import {getResourceErrorMessage, isResourceFailed} from "../../utils/resourceUtils";
import {ErrorAlert} from "../base/ErrorAlert";

export interface Props {
    orders: Order[];
    companyId: string;
    fields: Field[];
    refreshOrders: () => void;
    refreshFields: () => void;
}

const orderReadyForReview = (order: Order) => {
    const fields = order.fields;
    if (order.state !== OrderStatus.NEW || order.fields === undefined || fields.length === 0) {
        return false;
    } else {
        return true;
    }
};

const orderIsEditable = (order: Order) => {
    return order.state === OrderStatus.NEW;
};

interface OrderCompProps {
    order: Order;
    companyId: string;
    refreshOrders: () => void;
    fields: Field[];
    refreshFields: () => void;
}

const OrderComp: React.FC<OrderCompProps> = ({order, companyId, refreshOrders, fields, refreshFields}) => {
    const {t} = useTranslation();
    const {dateFormat} = useUserPreferences();
    const msUtils = useMeasureSystemUtils();
    const authenticatedUserResource = useAtomValue(authenticatedUserLoadable);
    const authenticatedUser = useResourceValueOrDefault(authenticatedUserResource, undefined);
    const isAdmin =
        authenticatedUser !== undefined &&
        isUserHaveAnyOfGlobalRoles(authenticatedUser, [UserGlobalRole.ADMIN, UserGlobalRole.CS]);
    const orderManagement = useOrderManagement();
    const [isExpanded, setIsExpanded] = useState(false);
    const [orderEditDialogOpened, setOrderEditDialogOpened] = useState<boolean>(false);

    const toggleExpand = () => setIsExpanded(!isExpanded);

    const onOrderEditDialogClose = (isSuccessful: boolean) => {
        setOrderEditDialogOpened(false);
        if (isSuccessful) {
            refreshOrders();
            refreshFields();
        }
    };

    const submitForReview = () => {
        orderManagement
            .changeStatus(companyId, order.id, OrderStatus.UNDER_REVIEW)
            .then(() => {
                refreshOrders();
            })
            .catch(swallowException);
    };

    const getOrderArea = (order: Order) => {
        return order.areaInHa ? msUtils.getArea(order.areaInHa).toFixed(2) : "0";
    };

    const downloadShapeFile = (companyId: string, orderId: string) => {
        orderManagement
            .downloadShapeFile(companyId, orderId)
            .then(() => {
                refreshOrders();
            })
            .catch(swallowException);
    };

    return (
        <>
            {isResourceFailed(orderManagement.resource) && (
                <ErrorAlert
                    message={getResourceErrorMessage(orderManagement.resource)!}
                    onClose={() => orderManagement.reset()}
                />
            )}
            <Stack direction="row" alignItems="center" justifyContent="flex-start">
                <Stack
                    direction="row"
                    alignItems="center"
                    useFlexGap
                    justifyContent="space-between"
                    spacing={6}
                    sx={{
                        width: "100%",
                        backgroundColor: "#F3F5FA",
                        borderBottomWidth: "1px",
                        borderBottomStyle: "solid",
                        borderBottomColor: "#E9EBF1",
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        useFlexGap
                        justifyContent="space-between"
                        sx={{p: 2}}
                        spacing={2}
                    >
                        <OrderStatusCircle order={order} />
                        <Stack direction="row" gap="0.5rem">
                            <IconButton
                                sx={{padding: 0}}
                                onClick={(e) => {
                                    if (fields && fields.length > 0) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        toggleExpand();
                                    }
                                }}
                            >
                                {isExpanded ? (
                                    <ExpandMoreIcon fontSize="small" color="primary" />
                                ) : (
                                    <ChevronRightIcon fontSize="small" color="primary" />
                                )}
                            </IconButton>

                            <Typography textTransform="uppercase" fontSize={14}>
                                {isAdmin ? (
                                    <Link to={`/company/${companyId}/order/${order.id}`}>
                                        {constructOrderNumber(order, t)}
                                    </Link>
                                ) : (
                                    constructOrderNumber(order, t)
                                )}
                            </Typography>
                        </Stack>

                        <OrderItem>{order.state}</OrderItem>
                        <OrderItem>
                            {getOrderArea(order)} {msUtils.getAreaUnits()}
                        </OrderItem>
                        <OrderItem>
                            {order.fields.length} {t("orders.orders_list_view.fields")}
                        </OrderItem>
                        {order.finalPrice > 0 && (
                            <OrderItem>
                                {t("orders.orders_list_view.price")}: {order.finalPrice},&euro;
                            </OrderItem>
                        )}
                        <OrderItem>
                            {t("orders.orders_list_view.subscription")}: {getOrderPeriod(order, dateFormat)}
                        </OrderItem>
                    </Stack>
                    <Box>
                        <IconButton
                            color={orderIsEditable(order) ? "primary" : "secondary"}
                            disabled={!orderIsEditable(order)}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setOrderEditDialogOpened(true);
                            }}
                        >
                            <EditIcon
                                sx={{
                                    width: "1.1rem",
                                    height: "1.1rem",
                                    stroke: "currentColor",
                                }}
                            />
                        </IconButton>
                        <IconButton
                            color="primary"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                downloadShapeFile(companyId, order.id);
                            }}
                        >
                            <DownloadIcon sx={{stroke: "#6476CB", width: "1.1rem", height: "1.1rem"}} />
                        </IconButton>
                    </Box>
                </Stack>
            </Stack>

            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <FieldsTable companyId={companyId} isAdmin={isAdmin} fields={fields} refresh={refreshFields} />

                <Stack direction="row" justifyContent="flex-end" sx={{padding: "1rem"}}>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<CheckIcon />}
                        disabled={!orderReadyForReview(order)}
                        onClick={submitForReview}
                    >
                        {t("orders.orders_list_view.submit_for_review")}
                    </Button>
                </Stack>
            </Collapse>

            {orderEditDialogOpened && companyId && order && (
                <OrderCreateDialog
                    mode="edit"
                    companyId={companyId}
                    selectedOrder={order}
                    open={orderEditDialogOpened}
                    onClose={onOrderEditDialogClose}
                />
            )}
        </>
    );
};

export const OrdersListView: React.FC<Props> = ({orders, refreshOrders, companyId, fields, refreshFields}) => {
    const authenticatedUserResource = useAtomValue(authenticatedUserLoadable);
    const authenticatedUser = useResourceValueOrDefault(authenticatedUserResource, undefined);
    const isAdmin =
        authenticatedUser !== undefined &&
        isUserHaveAnyOfGlobalRoles(authenticatedUser, [UserGlobalRole.ADMIN, UserGlobalRole.CS]);

    return (
        <div>
            <Stack direction="column" gap="1.125rem">
                {orders
                    .filter((order) => isAdmin || order.state !== OrderStatus.CANCELED)
                    .map((order) => {
                        const filteredFields = fields ? fields.filter((item) => item.orderId == order.id) : [];
                        return (
                            <OrderComp
                                key={order.id}
                                order={order}
                                fields={filteredFields}
                                refreshFields={refreshFields}
                                companyId={companyId}
                                refreshOrders={refreshOrders}
                            />
                        );
                    })}
            </Stack>
        </div>
    );
};

const OrderItem: React.FC<{children: React.ReactNode}> = ({children}) => {
    return (
        <Typography textTransform="uppercase" sx={{color: "#737990"}} fontSize={12}>
            {children}
        </Typography>
    );
};

const constructOrderNumber = (order: Order, t: TFunction<string, undefined>) => {
    //We pick first 8 digits of UUID. Later we use another auto-increment field of order
    const orderNumber = order.id.slice(0, 8);

    return t("orders.orders_list_view.order_number", {orderNumber});
};
